import React, { useState, useEffect, useCallback } from 'react';
import type { Showcase, ShowcaseProperty, ShowcaseResource } from '../../types';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';

interface LightboxProps {
  images: ShowcaseResource[];
  index: number;
  onClose: () => void;
  onNavigate: (index: number) => void;
}

function Lightbox({ images, index, onClose, onNavigate }: LightboxProps) {
  const goPrev = useCallback(() => { if (index > 0) onNavigate(index - 1); }, [index, onNavigate]);
  const goNext = useCallback(() => { if (index < images.length - 1) onNavigate(index + 1); }, [index, images.length, onNavigate]);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowLeft') goPrev();
      else if (e.key === 'ArrowRight') goNext();
    };
    document.addEventListener('keydown', handleKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = '';
    };
  }, [onClose, goPrev, goNext]);

  return (
    <div
      className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center"
      role="dialog"
      aria-modal="true"
      aria-label="Image lightbox"
      onClick={onClose}
    >
      <button
        className="absolute top-4 right-4 text-white/70 hover:text-white z-10"
        onClick={onClose}
        aria-label="Close lightbox"
      >
        <X className="w-8 h-8" />
      </button>

      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/50 text-sm z-10">
        {index + 1} / {images.length}
      </div>

      {index > 0 && (
        <button
          className="absolute left-4 text-white/70 hover:text-white z-10 p-2"
          onClick={(e) => { e.stopPropagation(); goPrev(); }}
          aria-label="Previous image"
        >
          <ChevronLeft className="w-10 h-10" />
        </button>
      )}
      {index < images.length - 1 && (
        <button
          className="absolute right-4 text-white/70 hover:text-white z-10 p-2"
          onClick={(e) => { e.stopPropagation(); goNext(); }}
          aria-label="Next image"
        >
          <ChevronRight className="w-10 h-10" />
        </button>
      )}
      <img
        src={images[index].source_url!}
        alt={images[index].title || `Photo ${index + 1}`}
        className="max-w-[90vw] max-h-[85vh] object-contain"
        onClick={(e) => e.stopPropagation()}
      />
    </div>
  );
}

interface GallerySectionProps {
  showcase: Showcase;
  property: ShowcaseProperty;
  resources: ShowcaseResource[];
  brandColour: string;
  darkMode?: boolean;
}

export default function GallerySection({ resources, darkMode: dm }: GallerySectionProps) {
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const images = resources.filter((r) => r.source_url);

  if (images.length === 0) return null;

  return (
    <section className={`py-16 transition-colors duration-300 ${dm ? 'bg-slate-900' : 'bg-white'}`}>
      <div className="max-w-6xl mx-auto px-8">
        <h2 className={`text-2xl font-bold mb-8 ${dm ? 'text-white' : 'text-slate-900'}`}>Gallery</h2>

        <div className="columns-1 sm:columns-2 lg:columns-3 gap-3 space-y-3">
          {images.map((img, idx) => (
            <button
              key={img.id}
              onClick={() => setLightboxIndex(idx)}
              className="relative w-full overflow-hidden rounded-lg group cursor-pointer break-inside-avoid block"
            >
              <img
                src={img.source_url!}
                alt={img.title || `Photo ${idx + 1}`}
                className="w-full h-auto block transition-transform duration-300 group-hover:scale-[1.02]"
              />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors rounded-lg" />
            </button>
          ))}
        </div>
      </div>

      {lightboxIndex !== null && (
        <Lightbox
          images={images}
          index={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
          onNavigate={setLightboxIndex}
        />
      )}
    </section>
  );
}
